import { showNotification } from '@helpers/notification-helper';
import { useAppDispatch, useAppSelector } from '@hooks/app/redux';
import { updateAddresses, updateProfile } from '@services/external/user.api';
import { actions } from '@store/ducks/users';
import {
  Button,
  Col,
  CountrySelect,
  DateField,
  Form,
  MessageBox,
  Row,
  TextField,
  Typography,
} from '@ui';
import moment from 'moment';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useTheme } from 'styled-components';
import OnboardingLayout from '../../../../layout/onboarding-layout';
import KycFooter from '../kyc-footer';
import { useInitialValues, validationSchema } from './kyc-profile.schema';
import { FormSC } from './kyc-profile.styled';

const gve = (v: any) => v;

interface KycProfileProps {
  afterSubmit?: () => void;
  onBack?: () => void;
}

const KycProfile: React.FunctionComponent<KycProfileProps> = ({ afterSubmit, onBack }) => {
  const user = useAppSelector((state) => state.users.currentUser)!;
  const dispatch = useAppDispatch();
  const initialValues = useInitialValues();
  const { formatMessage } = useIntl();
  const theme = useTheme();

  const onError = (err: any) => {
    const errCode = err.data?.data?.errCode;
    const message = errCode ? `server_error.${errCode}` : 'server_error.something_wrong';

    showNotification({
      type: 'ERROR',
      title: <FormattedMessage id="profie_edit.error_alert.title" />,
      copy: <FormattedMessage id={message} />,
    });
  };

  const onSuccess = () => {
    dispatch(actions.getCurrentUserRequest());

    showNotification({
      type: 'SUCCESS',
      title: <FormattedMessage id="profile_edit.success_alert.title" />,
      copy: '',
    });

    afterSubmit?.();
  };

  const [, formOptions] = Form.useForm({
    initialValues,
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (v) => {
      try {
        const permanentAddress = user.addresses.find(
          (address: any) => address.type === 'permanent',
        );
        const actualAddress = user.addresses.find((address: any) => address.type === 'actual');

        const addresses = [
          {
            ...permanentAddress,
            city: v.city,
            country: v.country,
            postCode: v.postalCode,
            address: v.address,
            type: 'permanent',
          },
        ];

        if (actualAddress) {
          addresses.push(actualAddress);
        }

        await Promise.all([
          updateProfile({ ...v, birthDate: moment(v.birthDate).format('YYYY-MM-DD') }),
          updateAddresses(addresses),
        ]);

        onSuccess();
      } catch (error) {
        onError(error);
      }
    },
  });

  return (
    <OnboardingLayout footer={<KycFooter />}>
      <FormSC {...formOptions} showFeedback={true}>
        <Typography display="h3">{formatMessage({ id: 'kyc.formTitle' })}</Typography>
        <Form.Section label={formatMessage({ id: 'kyc.personalLabel' })}>
          <Row gutter={[24, 0]}>
            <Col xs={12} md={6}>
              <Form.Field
                name="firstName"
                label={formatMessage({ id: 'kyc.firstNameLabel' })}
                required
              >
                <TextField autoComplete="given-name" />
              </Form.Field>
            </Col>
            <Col xs={12} md={6}>
              <Form.Field
                name="lastName"
                label={formatMessage({ id: 'kyc.lastNameLabel' })}
                required
              >
                <TextField autoComplete="family-name" />
              </Form.Field>
            </Col>
          </Row>
          <Row className="date-row">
            <Col xs={12} sm={12} md={12} xl={8}>
              <Form.Field
                name="birthDate"
                label={formatMessage({ id: 'kyc.birthDateLabel' })}
                required
                getValueFromEvent={(e) => e}
              >
                <DateField />
              </Form.Field>
            </Col>
          </Row>
          <Form.Field
            name="pin"
            label={formatMessage({ id: 'kyc.pinLabel' })}
            required
            caption={formatMessage({ id: 'kyc.pinCaption' })}
          >
            <TextField />
          </Form.Field>
        </Form.Section>
        <Form.Section label={formatMessage({ id: 'kyc.addressSectionLabel' })}>
          <Form.Field name="address" label={formatMessage({ id: 'kyc.addressLabel' })} required>
            <TextField autoComplete="street-address" />
          </Form.Field>
          <Row gutter={[24, 0]}>
            <Col xs={12} md={6}>
              <Form.Field name="city" label={formatMessage({ id: 'kyc.cityLabel' })} required>
                <TextField autoComplete="address-line1" />
              </Form.Field>
            </Col>
            <Col xs={12} md={6}>
              <Form.Field
                name="postalCode"
                label={formatMessage({ id: 'kyc.postalLabel' })}
                required
              >
                <TextField autoComplete="postal-code" />
              </Form.Field>
            </Col>
          </Row>
          <Form.Field
            name="country"
            label={formatMessage({ id: 'kyc.countyLabel' })}
            required
            getValueFromEvent={gve}
          >
            <CountrySelect useAuthCountries />
          </Form.Field>
        </Form.Section>
        <MessageBox
          variant="warning"
          style={{ marginTop: 40 }}
          title={formatMessage({ id: 'kyc.disclaimerTitle' })}
          description={formatMessage({ id: 'kyc.disclaimerDescription' })}
        />
        <Form.Custom>
          {(f) => (
            <Row
              justify="end"
              style={{ marginTop: theme.spacings.component.L }}
              gutter={theme.spacings.component.S}
            >
              <Col xs={6}>
                <Button size="large" variant="ghost" onClick={onBack} leftArrow block={false}>
                  {formatMessage({ id: 'kyc.backAction' })}
                </Button>
              </Col>
              <Col xs={6}>
                <Button
                  size="large"
                  variant="primary"
                  onClick={() => {
                    f.submitForm();
                  }}
                  loading={f.isSubmitting}
                  rightArrow
                >
                  {formatMessage({ id: 'kyc.submitAction' })}
                </Button>
              </Col>
            </Row>
          )}
        </Form.Custom>
      </FormSC>
    </OnboardingLayout>
  );
};

export default KycProfile;
